import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export type CommentLinkPlacement = "public" | "dm_only";
export type CommentReplyMode = "ai_decides" | "always_both" | "public_only";

const REPLY_OPTIONS: { value: CommentReplyMode; label: string; description: string }[] = [
  {
    value: "always_both",
    label: "Sempre comentário e DM",
    description: "Toda resposta sai no post e no direct. Exige Private Reply ligado na conta do Instagram.",
  },
  {
    value: "ai_decides",
    label: "A IA decide",
    description: "Responde no comentário. A DM sai só em preço, link marcado como somente DM, e crítica.",
  },
  {
    value: "public_only",
    label: "Só comentário público",
    description: "Nenhuma DM é enviada a partir do comentário, mesmo com Private Reply ligado.",
  },
];

const OPTIONS: { value: CommentLinkPlacement; label: string; description: string }[] = [
  {
    value: "public",
    label: "No comentário público",
    description: "O link pode aparecer na resposta visível no post.",
  },
  {
    value: "dm_only",
    label: "Somente na DM",
    description: "O link vai no direct. Sem Private Reply, a resposta pública não leva a URL.",
  },
];

function PlacementField<T extends string>({
  name,
  title,
  value,
  options,
  onChange,
}: {
  name: string;
  title: string;
  value: T;
  options: { value: T; label: string; description: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-sm">{title}</Label>
      <RadioGroup
        value={value}
        onValueChange={(next) => onChange(next as T)}
        className="grid gap-2"
      >
        {options.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 p-3"
          >
            <RadioGroupItem value={option.value} id={`${name}-${option.value}`} className="mt-0.5" />
            <span className="space-y-0.5">
              <span className="block text-sm font-medium">{option.label}</span>
              <span className="block text-xs text-muted-foreground">{option.description}</span>
            </span>
          </label>
        ))}
      </RadioGroup>
    </div>
  );
}

export function CommentReplySection({
  replyMode,
  websitePlacement,
  productPlacement,
  onReplyModeChange,
  onWebsitePlacementChange,
  onProductPlacementChange,
}: {
  replyMode: CommentReplyMode;
  websitePlacement: CommentLinkPlacement;
  productPlacement: CommentLinkPlacement;
  onReplyModeChange: (value: CommentReplyMode) => void;
  onWebsitePlacementChange: (value: CommentLinkPlacement) => void;
  onProductPlacementChange: (value: CommentLinkPlacement) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        O site usado é o cadastrado em Redes sociais. O link de produto vem só da busca daquele comentário.
        Preço não entra no comentário público. A DM só sai se a conta tiver Private Reply ligado.
      </p>
      <PlacementField
        name="reply-mode"
        title="Como responder"
        value={replyMode}
        options={REPLY_OPTIONS}
        onChange={onReplyModeChange}
      />
      <PlacementField
        name="website"
        title="Site da empresa"
        value={websitePlacement}
        options={OPTIONS}
        onChange={onWebsitePlacementChange}
      />
      <PlacementField
        name="product"
        title="Link de produto"
        value={productPlacement}
        options={OPTIONS}
        onChange={onProductPlacementChange}
      />
    </div>
  );
}
